/**
 * Administrarea abonamentelor și a încasărilor — perspectiva platformei.
 *
 * Ecranul este construit pentru operațiunea reală a unei echipe mici care vinde un SaaS:
 *  • ce trebuie făcut ASTĂZI (restanțe, plăți de confirmat) — de aceea lista se sortează cu
 *    restanțele primele, nu alfabetic;
 *  • ce se întâmplă cu un client anume (plan, discount, zile de probă, perioadă);
 *  • încasările care intră prin transfer bancar, confirmate manual de contabilitate.
 *
 * Nicio acțiune nu șterge date: „trecerea pe plan gratuit" este o schimbare de limite, nu o
 * pierdere de date, iar asta este vizibil și în textul ecranului.
 */
import { useMemo, useState } from 'react';
import { Badge, Button, InlineAlert, Modal, Select, useToast } from '../../components/ui.js';
import { AdminCard, AdminFilterInput, AdminHeader, AdminRow, AdminTable, AdminTableSkeleton, DarkKpi, Td } from './AdminKit.js';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate, formatDateTime, formatNumber, useResource } from '../../lib/hooks.js';
import { errorMessage } from '../../lib/session.js';

interface SubscriptionRow {
  id: string;
  accountId: string;
  accountName: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused';
  planName: string;
  planCode: string;
  priceCents: number;
  currency: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  openInvoices: number;
  overdueCents: number;
}

interface AdminTotals {
  active: number;
  trialing: number;
  pastDue: number;
  mrrCents: number;
  overdueCents: number;
}

interface InvoiceRow {
  id: string;
  number: string;
  accountName: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  totalCents: number;
  currency: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  overdue: boolean;
  /** Perioada pe care factura o documentează — verificarea rapidă a facturilor emise automat. */
  periodStart: string | null;
  periodEnd: string | null;
}

interface PlanOption {
  id: string;
  code: string;
  name: string;
  priceCents: number;
  currency: string;
}

const SUBSCRIPTION_TONE: Record<SubscriptionRow['status'], 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  trialing: 'info',
  active: 'success',
  past_due: 'warning',
  canceled: 'neutral',
  paused: 'warning',
};

export function AdminSubscriptionsPage() {
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<SubscriptionRow | null>(null);
  const [busy, setBusy] = useState(false);

  const [planCode, setPlanCode] = useState('');
  const [extendTrialDays, setExtendTrialDays] = useState('0');
  const [discountPercent, setDiscountPercent] = useState('');
  const [reason, setReason] = useState('');

  // Un singur loc care construiește adresa listei: aceeași sursă pentru cerere și pentru orice altă
  // folosire viitoare (evită cele două variante ale aceleiași interogări).
  const subscriptionsUrl = (() => {
    const q = query.trim();
    return `/admin/subscriptions?limit=100${status ? `&status=${status}` : ''}${q ? `&query=${encodeURIComponent(q)}` : ''}`;
  })();
  const subscriptions = useResource<{ subscriptions: SubscriptionRow[]; totals: AdminTotals }>(
    () => api.get(subscriptionsUrl),
    [status, query],
  );
  const plans = useResource<PlanOption[]>(() => api.get<PlanOption[]>('/admin/plans'));

  const rows = subscriptions.data?.subscriptions ?? [];
  const totals = subscriptions.data?.totals;

  async function applyChange() {
    if (!editing || !planCode) return;
    setBusy(true);
    try {
      await api.post(`/admin/subscriptions/${editing.accountId}/plan`, {
        planCode,
        extendTrialDays: Number(extendTrialDays) || undefined,
        discountPercent: discountPercent === '' ? undefined : Number(discountPercent),
        reason: reason.trim() || undefined,
      });
      toast.success('Abonament actualizat', `${editing.accountName} folosește acum planul ${planCode}.`);
      setEditing(null);
      await subscriptions.reload();
    } catch (error) {
      toast.error('Modificarea nu a reușit', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /** Verificarea manuală a ciclului de viață: utilă înainte de o campanie de reînnoire. */
  async function runLifecycle() {
    setBusy(true);
    try {
      const result = await api.post<{ checked: number; invoices: number; notices: number; transitions: Array<unknown> }>(
        '/admin/billing/run-lifecycle',
        {},
      );
      toast.success(
        'Ciclu de viață rulat',
        `${result.checked} abonamente verificate, ${result.invoices} facturi emise, ${result.transitions.length} tranziții.`,
      );
      await subscriptions.reload();
    } catch (error) {
      toast.error('Rularea nu a reușit', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <AdminHeader
        title="Abonamente"
        subtitle="Cine plătește, cine întârzie și ce se întâmplă automat. Ajustările se fac aici, fără intervenția în baza de date."
        actions={
          <>
            <AdminFilterInput value={query} onChange={setQuery} placeholder="Caută după cont sau email…" />
            <Select value={status} onChange={(event) => setStatus(event.target.value)} className="h-10 border-white/15 bg-white/5 text-white">
              <option value="">Toate stările</option>
              <option value="trialing">În probă</option>
              <option value="active">Active</option>
              <option value="past_due">Plată în așteptare</option>
              <option value="paused">În pauză</option>
              <option value="canceled">Anulate</option>
            </Select>
            <Button variant="secondary" size="sm" loading={busy} onClick={() => void runLifecycle()}>
              Rulează ciclul de viață
            </Button>
          </>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DarkKpi label="Active" value={formatNumber(totals?.active ?? 0)} tone="brand" hint="abonamente care se reînnoiesc" />
        <DarkKpi label="În perioadă de probă" value={formatNumber(totals?.trialing ?? 0)} hint="fără plată încă" />
        <DarkKpi
          label="MRR (Recurrent)"
          value={formatCurrency(totals?.mrrCents ?? 0, 'RON')}
          hint="valoare lunară recurentă"
        />
        <DarkKpi
          label="Neîncasat"
          value={formatCurrency(totals?.overdueCents ?? 0, 'RON')}
          hint={totals?.pastDue ? `${totals.pastDue} conturi cu plată în așteptare` : 'toate plățile la zi'}
        />
      </div>

      <AdminCard>
        {subscriptions.loading ? (
          <AdminTableSkeleton rows={5} />
        ) : subscriptions.error ? (
          <InlineAlert tone="danger" title="Abonamentele nu au putut fi încărcate">
            {subscriptions.error}
          </InlineAlert>
        ) : !rows.length ? (
          <p className="py-8 text-center text-body-s text-ink-400">Niciun abonament pentru filtrele curente.</p>
        ) : (
          <AdminTable headers={['Cont', 'Plan', 'Stare', 'Perioada curentă', 'Facturi deschise', 'Restanță', '']}>
            {rows.map((row) => (
              <AdminRow key={row.id} tone={row.overdueCents > 0 ? 'danger' : undefined}>
                <Td className="text-white">{row.accountName}</Td>
                <Td>
                  <span className="flex flex-col">
                    <span>{row.planName}</span>
                    <span className="nums text-caption text-ink-400">{formatCurrency(row.priceCents, row.currency)}</span>
                  </span>
                </Td>
                <Td>
                  <Badge tone={SUBSCRIPTION_TONE[row.status]}>{row.status}</Badge>
                  {row.cancelAtPeriodEnd ? <span className="ml-2 text-caption text-ink-400">se încheie</span> : null}
                </Td>
                <Td>{formatDateTime(row.currentPeriodEnd)}</Td>
                <Td>
                  <span className="nums">{row.openInvoices}</span>
                </Td>
                <Td>
                  <span className={row.overdueCents > 0 ? 'nums font-medium text-danger' : 'nums text-ink-400'}>
                    {formatCurrency(row.overdueCents, row.currency)}
                  </span>
                </Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(row);
                      setPlanCode(row.planCode);
                      setExtendTrialDays('0');
                      setDiscountPercent('');
                      setReason('');
                    }}
                  >
                    Modifică
                  </Button>
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Abonamentul contului ${editing.accountName}` : ''}
        description="Planul, discountul și zilele de probă se aplică imediat. Datele clientului nu sunt afectate."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Renunță
            </Button>
            <Button loading={busy} disabled={!planCode} onClick={() => void applyChange()}>
              Aplică modificarea
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="plan-code" className="text-body-s font-medium text-ink-800">
              Plan
            </label>
            <Select id="plan-code" value={planCode} onChange={(event) => setPlanCode(event.target.value)}>
              {(plans.data ?? []).map((plan) => (
                <option key={plan.id} value={plan.code}>
                  {plan.name} — {plan.priceCents === 0 ? 'gratuit' : `${formatCurrency(plan.priceCents, plan.currency)} / lună`}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="trial-days" className="text-body-s font-medium text-ink-800">
                Zile de probă (opțional)
              </label>
              <input
                id="trial-days"
                type="number"
                min={0}
                max={120}
                value={extendTrialDays}
                onChange={(event) => setExtendTrialDays(event.target.value)}
                className="h-11 rounded-[var(--radius-md)] border border-ink-200 px-3.5 text-body"
              />
              <p className="text-caption text-ink-400">Extinde proba fără a schimba perioada plătită.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="discount" className="text-body-s font-medium text-ink-800">
                Discount (%)
              </label>
              <input
                id="discount"
                type="number"
                min={0}
                max={100}
                value={discountPercent}
                onChange={(event) => setDiscountPercent(event.target.value)}
                placeholder="fără discount"
                className="h-11 rounded-[var(--radius-md)] border border-ink-200 px-3.5 text-body"
              />
              <p className="text-caption text-ink-400">Se aplică automat la facturile următoare.</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="reason" className="text-body-s font-medium text-ink-800">
              Motiv (rămâne în jurnalul de audit)
            </label>
            <input
              id="reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="ex. parteneriat local, migrare din altă platformă"
              className="h-11 rounded-[var(--radius-md)] border border-ink-200 px-3.5 text-body"
            />
          </div>

          <InlineAlert tone="info" title="Ce se întâmplă automat">
            La finalul perioadei se emite factura următoarei perioade. Dacă plata nu sosește în termenul de grație,
            contul trece pe planul gratuit — meniurile, codurile QR și statisticile rămân intacte.
          </InlineAlert>
        </div>
      </Modal>
    </div>
  );
}

export function AdminInvoicesPage() {
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [paying, setPaying] = useState<InvoiceRow | null>(null);
  const [method, setMethod] = useState<'transfer' | 'cash' | 'card' | 'other'>('transfer');
  const [busy, setBusy] = useState(false);

  const invoices = useResource<{ invoices: InvoiceRow[] }>(
    () => api.get(`/admin/invoices?limit=120${status ? `&status=${status}` : ''}`),
    [status],
  );

  const rows = invoices.data?.invoices ?? [];
  const summary = useMemo(() => {
    const open = rows.filter((row) => row.status === 'open');
    return {
      open: open.length,
      overdue: open.filter((row) => row.overdue).length,
      openCents: open.reduce((total, row) => total + row.totalCents, 0),
      paidThisList: rows.filter((row) => row.status === 'paid').reduce((total, row) => total + row.totalCents, 0),
    };
  }, [rows]);

  async function markPaid() {
    if (!paying) return;
    setBusy(true);
    try {
      const result = await api.post<{ invoiceId: string; subscriptionStatus: string | null }>(`/admin/invoices/${paying.id}/mark-paid`, { method });
      toast.success(
        'Plată înregistrată',
        result.subscriptionStatus
          ? `Factura ${paying.number} este achitată, iar abonamentul contului ${paying.accountName} este din nou activ.`
          : `Factura ${paying.number} este achitată.`,
      );
      setPaying(null);
      await invoices.reload();
    } catch (error) {
      toast.error('Plata nu a putut fi înregistrată', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <AdminHeader
        title="Facturi și încasări"
        subtitle="Facturile se emit automat la începutul fiecărei perioade; aici se confirmă încasările prin transfer bancar."
        actions={
          <Select value={status} onChange={(event) => setStatus(event.target.value)} className="h-10 border-white/15 bg-white/5 text-white">
            <option value="">Toate facturile</option>
            <option value="open">De achitat</option>
            <option value="paid">Achitate</option>
            <option value="draft">Ciornă</option>
            <option value="void">Anulate</option>
          </Select>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DarkKpi label="Facturi de achitat" value={formatNumber(summary.open)} tone="brand" hint="emise, încă neachitate" />
        <DarkKpi label="Restante" value={formatNumber(summary.overdue)} hint="au depășit scadența" />
        <DarkKpi label="Valoare de încasat" value={formatCurrency(summary.openCents, 'RON')} hint="total pe facturile deschise" />
        <DarkKpi label="Încasat (listă)" value={formatCurrency(summary.paidThisList, 'RON')} hint="facturile achitate afișate" />
      </div>

      <AdminCard>
        {invoices.loading ? (
          <AdminTableSkeleton rows={6} />
        ) : invoices.error ? (
          <InlineAlert tone="danger" title="Facturile nu au putut fi încărcate">
            {invoices.error}
          </InlineAlert>
        ) : !rows.length ? (
          <p className="py-8 text-center text-body-s text-ink-400">
            Nicio factură pentru filtrul curent. Facturile apar automat la începutul perioadei facturate.
          </p>
        ) : (
          <AdminTable headers={['Factură', 'Cont', 'Perioadă', 'Emisă', 'Scadentă', 'Total', 'Stare', '']}>
            {rows.map((row) => (
              <AdminRow key={row.id} tone={row.overdue ? 'danger' : undefined}>
                <Td className="font-mono text-caption text-white">{row.number}</Td>
                <Td>{row.accountName}</Td>
                <Td className="whitespace-nowrap text-caption text-ink-300">
                  {row.periodStart && row.periodEnd
                    ? `${formatDate(row.periodStart)} – ${formatDate(row.periodEnd)}`
                    : '—'}
                </Td>
                <Td>{row.issuedAt ? formatDateTime(row.issuedAt) : '—'}</Td>
                <Td>{row.dueAt ? formatDateTime(row.dueAt) : '—'}</Td>
                <Td className="nums">{formatCurrency(row.totalCents, row.currency)}</Td>
                <Td>
                  <Badge tone={row.status === 'paid' ? 'success' : row.overdue ? 'danger' : 'warning'}>
                    {row.overdue ? 'restantă' : row.status}
                  </Badge>
                </Td>
                <Td className="text-right">
                  {row.status === 'open' ? (
                    <Button size="sm" variant="secondary" onClick={() => setPaying(row)}>
                      Înregistrează plata
                    </Button>
                  ) : row.paidAt ? (
                    <span className="text-caption text-ink-400">achitată {formatDateTime(row.paidAt)}</span>
                  ) : null}
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <Modal
        open={paying !== null}
        onClose={() => setPaying(null)}
        title={paying ? `Înregistrezi plata facturii ${paying.number}` : ''}
        description="Confirmarea înseamnă că banii au intrat în cont. Abonamentul contului devine activ imediat, cu perioadă nouă."
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaying(null)}>
              Renunță
            </Button>
            <Button loading={busy} onClick={() => void markPaid()}>
              Confirmă încasarea
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="nums text-display-m font-display text-ink-950">
            {paying ? formatCurrency(paying.totalCents, paying.currency) : ''}
          </p>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="method" className="text-body-s font-medium text-ink-800">
              Metoda de plată
            </label>
            <Select id="method" value={method} onChange={(event) => setMethod(event.target.value as typeof method)}>
              <option value="transfer">Transfer bancar</option>
              <option value="cash">Numerar</option>
              <option value="card">Card</option>
              <option value="other">Altă metodă</option>
            </Select>
          </div>
          <InlineAlert tone="info" title="Plata online (Stripe)">
            Când cheile furnizorului de plăți sunt configurate, clienții plătesc singuri pe card, iar factura se
            închide automat la confirmarea webhook-ului. Fluxul există deja; lipsește doar activarea.
          </InlineAlert>
        </div>
      </Modal>
    </div>
  );
}
